import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MetricRendererProps } from './rendererTypes';
import { getMetricDisplayName } from './rendererTypes';
import TileMetaActions from '../TileMetaActions';
import { tileMetaTitlePad } from '../../utils/tileMeta';

type TickProps = {
  x?: number;
  y?: number;
  index?: number;
  payload?: { value?: string };
};

type BreakdownItem = {
  label: string;
  value: number;
};

const Y_AXIS_WIDTH = 48;
const LABEL_FONT_SIZE = 11;
const LABEL_LINE_HEIGHT = 14;
const COLUMN_GAP = 12;
const MAX_WRAP_LINES = 3;

let measureCanvas: HTMLCanvasElement | null = null;

function measureTextWidth(text: string, font: string): number {
  if (typeof document === 'undefined') return text.length * 7;
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const context = measureCanvas.getContext('2d');
  if (!context) return text.length * 7;
  context.font = font;
  return Math.ceil(context.measureText(text).width) + 2;
}

/** Split a label into lines that each fit `maxWidth`. Null when a line would still collide. */
function wrapLabel(label: string, maxWidth: number, font: string): string[] | null {
  if (maxWidth <= 0) return null;
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];

  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (measureTextWidth(word, font) > maxWidth) return null;
    const next = current ? `${current} ${word}` : word;
    if (measureTextWidth(next, font) <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (lines.length > MAX_WRAP_LINES) return null;
  return lines;
}

function chooseAngle(slotWidth: number, labelWidth: number): number {
  const minSeparation = LABEL_FONT_SIZE + 6;
  let angle = 50;
  const separation = (degrees: number) =>
    slotWidth * Math.sin((degrees * Math.PI) / 180);
  while (angle < 70 && separation(angle) < minSeparation) {
    angle += 5;
  }
  if (slotWidth > 0 && labelWidth > slotWidth * 1.6 && angle < 58) {
    return 58;
  }
  return angle;
}

type TickLayout =
  | { mode: 'inline'; axisHeight: number; chartWidth: number; rightMargin: number }
  | {
      mode: 'wrapped';
      axisHeight: number;
      chartWidth: number;
      rightMargin: number;
      lines: string[][];
      valueY: number;
      deltaY: number;
    }
  | {
      mode: 'angled';
      axisHeight: number;
      chartWidth: number;
      rightMargin: number;
      angle: number;
      slotWidth: number;
    }
  | { mode: 'scroll'; axisHeight: number; chartWidth: number; rightMargin: number };

function resolveTickLayout(
  plotWidth: number,
  labels: string[],
  font: string,
): TickLayout {
  const count = labels.length;
  const inline = {
    mode: 'inline' as const,
    axisHeight: 64,
    chartWidth: plotWidth,
    rightMargin: 8,
  };
  if (count === 0 || plotWidth <= 0) return inline;

  const widths = labels.map(label => measureTextWidth(label, font));
  const widest = Math.max(...widths);
  const inlineSlot = (plotWidth - Y_AXIS_WIDTH - inline.rightMargin) / count;

  if (widest + COLUMN_GAP <= inlineSlot) return inline;

  const wrapped = labels.map(label => wrapLabel(label, inlineSlot - COLUMN_GAP, font));
  if (wrapped.every((lines): lines is string[] => lines != null)) {
    const maxLines = Math.max(1, ...wrapped.map(lines => lines.length));
    const lastBaseline = 14 + (maxLines - 1) * LABEL_LINE_HEIGHT;
    const valueY = lastBaseline + 18;
    const deltaY = valueY + 16;
    return {
      mode: 'wrapped',
      axisHeight: deltaY + 10,
      chartWidth: plotWidth,
      rightMargin: inline.rightMargin,
      lines: wrapped,
      valueY,
      deltaY,
    };
  }

  const angle = chooseAngle(inlineSlot, widest);
  const radians = (angle * Math.PI) / 180;
  const endX = -inlineSlot / 2 + widest * Math.cos(radians);
  const rightMargin = Math.max(8, Math.ceil(endX + 12));
  const slotWidth = Math.max(1, (plotWidth - Y_AXIS_WIDTH - rightMargin) / count);
  const finalAngle = chooseAngle(slotWidth, widest);
  const finalRadians = (finalAngle * Math.PI) / 180;
  const separation = slotWidth * Math.sin(finalRadians);
  const numbersFit = slotWidth >= 36;

  if (separation >= LABEL_FONT_SIZE + 6 && numbersFit) {
    const depth = widest * Math.sin(finalRadians);
    return {
      mode: 'angled',
      axisHeight: Math.ceil(46 + depth + 12),
      chartWidth: plotWidth,
      rightMargin,
      angle: finalAngle,
      slotWidth,
    };
  }

  const minSlot = Math.ceil(widest + COLUMN_GAP + 8);
  return {
    mode: 'scroll',
    axisHeight: 64,
    chartWidth: Math.max(plotWidth, count * minSlot + Y_AXIS_WIDTH + 8),
    rightMargin: 8,
  };
}

export default function VerticalBarChartRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(0);
  const title = getMetricDisplayName(data, config);
  const items: BreakdownItem[] = (data.breakdown ?? []).map(item => ({
    label: item.label,
    value: item.value,
  }));
  const chartData = items.map(item => ({
    name: item.label,
    value: item.value,
  }));
  const fontFamily = theme.typography.fontFamily || 'sans-serif';
  const labelFont = `${LABEL_FONT_SIZE}px ${fontFamily}`;
  const layout = resolveTickLayout(
    plotWidth,
    items.map(item => item.label),
    labelFont,
  );
  const chartHeight = 208 + layout.axisHeight;

  useLayoutEffect(() => {
    const element = plotRef.current;
    if (!element) return undefined;
    const update = () => setPlotWidth(element.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const renderTick = ({ x = 0, y = 0, index = 0 }: TickProps) => {
    const item = items[index];
    if (!item) return <g transform={`translate(${x},${y})`} />;

    const nameFill = theme.palette.text.secondary;
    const valueFill = theme.palette.text.primary;
    const deltaFill = theme.palette.success.main;

    if (layout.mode === 'wrapped') {
      const lines = layout.lines[index] ?? [item.label];
      return (
        <g transform={`translate(${x},${y})`}>
          <text textAnchor="middle" fill={nameFill} fontSize={LABEL_FONT_SIZE} fontFamily={fontFamily}>
            {lines.map((line, lineIndex) => (
              <tspan key={`${index}-${lineIndex}`} x={0} dy={lineIndex === 0 ? 14 : LABEL_LINE_HEIGHT}>
                {line}
              </tspan>
            ))}
          </text>
          <text
            y={layout.valueY}
            textAnchor="middle"
            fill={valueFill}
            fontSize={13}
            fontWeight={700}
            fontFamily={fontFamily}
          >
            {item.value}
          </text>
          <text
            y={layout.deltaY}
            textAnchor="middle"
            fill={deltaFill}
            fontSize={11}
            fontWeight={600}
            fontFamily={fontFamily}
          >
            +{item.value}
          </text>
        </g>
      );
    }

    if (layout.mode === 'angled') {
      const labelX = -layout.slotWidth / 2 + 4;
      const labelY = 46;
      return (
        <g transform={`translate(${x},${y})`}>
          <text
            y={16}
            textAnchor="middle"
            fill={valueFill}
            fontSize={13}
            fontWeight={700}
            fontFamily={fontFamily}
          >
            {item.value}
          </text>
          <text
            y={32}
            textAnchor="middle"
            fill={deltaFill}
            fontSize={11}
            fontWeight={600}
            fontFamily={fontFamily}
          >
            +{item.value}
          </text>
          <text
            x={labelX}
            y={labelY}
            textAnchor="start"
            fill={nameFill}
            fontSize={LABEL_FONT_SIZE}
            fontFamily={fontFamily}
            transform={`rotate(${layout.angle}, ${labelX}, ${labelY})`}
          >
            {item.label}
          </text>
        </g>
      );
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={14}
          textAnchor="middle"
          fill={nameFill}
          fontSize={LABEL_FONT_SIZE}
          fontFamily={fontFamily}
        >
          {item.label}
        </text>
        <text
          x={0}
          y={0}
          dy={32}
          textAnchor="middle"
          fill={valueFill}
          fontSize={13}
          fontWeight={700}
          fontFamily={fontFamily}
        >
          {item.value}
        </text>
        <text
          x={0}
          y={0}
          dy={48}
          textAnchor="middle"
          fill={deltaFill}
          fontSize={11}
          fontWeight={600}
          fontFamily={fontFamily}
        >
          +{item.value}
        </text>
      </g>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        width: '100%',
        position: 'relative',
      }}
    >
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1.5,
            lineHeight: 1.3,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box
        ref={plotRef}
        sx={{
          width: '100%',
          overflowX: layout.mode === 'scroll' ? 'auto' : 'visible',
        }}
      >
        <Box sx={{ width: layout.mode === 'scroll' ? layout.chartWidth : '100%', height: chartHeight }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 8, right: layout.rightMargin, left: 0, bottom: 4 }}
            >
              <XAxis
                dataKey="name"
                interval={0}
                tick={renderTick}
                height={layout.axisHeight}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                width={Y_AXIS_WIDTH}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              />
              <Bar dataKey="value" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
}
